import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteDefaultQueryStateFromTable } from 'components/table/utlil';
import {
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';
import { isRefreshSelector, setIsRefreshLearners, useLearnerStore } from 'stores';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Learner } from '../type';

import { trimValue } from 'helpers';
import { useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { debounce, isEmpty, isUndefined } from 'lodash';

import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useLearnerController = () => {
  const userProfile = useAuthStore(userProfileSelector);

  const isRefresh = useLearnerStore(isRefreshSelector);

  const institutionId = userProfile?.institution?.id || '';

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.LEARNERS]);
  const navigateTo = useNavigate();
  const [learners, setLearners] = useState<Learner[]>([]);

  const total = useNumber(0);
  const learnerIdSelected = useString('');

  const searchValue = useString('');

  useEffect(() => {
    updateQueryStateTable(TABLE_KEY.LEARNERS, initialQueryState);

    return () => {
      updateQueryStateTable(TABLE_KEY.LEARNERS, {
        ...initialQueryState,
        search: '',
      });
    };
  }, []);

  useEffect(() => {
    if (isEmpty(userProfile)) {
      return;
    }

    if (queryState || isRefresh) {
      getList();

      setIsRefreshLearners(false);
    }
  }, [JSON.stringify(queryState), userProfile, isRefresh]);

  const getList = async () => {
    const result: {
      items: Learner[];
      total: number;
    } = await apiCall({
      url: '/users',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...deleteDefaultQueryStateFromTable(queryState),
        roleKey: RoleKey.LEARNER,
        institutionId,
      },
    });

    setLearners(result?.items ?? []);

    if (!isUndefined(result?.total)) {
      total.setValue(result.total);
    }
  };

  const onClickRow = (row: any) => {
    const original: Learner = row?.original;

    if (original?.id) {
      redirectToDetail(original.id);
    }
  };

  const onClickAction = async (key: 'edit' | 'delete' | 'update-status', learnerId: string) => {
    switch (key) {
      case 'edit':
        redirectToDetail(learnerId);
        break;

      case 'delete':
        learnerIdSelected.setValue(learnerId);
        openModal(MODAL_KEY.DELETE_LEARNER);
        break;

      case 'update-status':
        await apiCall({
          url: `/users/${learnerId}/status`,
          method: 'PATCH',
          isLoading: true,
          showError: true,
        });

        showToastMessage('success', `Updated client's status successfully!`);

        getList();
        break;

      default:
        break;
    }
  };

  const redirectToDetail = (id: string) => {
    navigateTo(`${PATH.LEARNERS}/${PATH.LEARNER_EDIT.replace(':id', id)}`);
  };

  const onSubmitDeleteLearner = async () => {
    if (learnerIdSelected.value) {
      await apiCall({
        url: `/users/${learnerIdSelected.value}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });
      closeModal(MODAL_KEY.DELETE_LEARNER);

      showToastMessage('success', 'Removed Client successfully!');

      if (queryState.page === 1) {
        getList();

        return;
      }

      updateQueryStateTable(TABLE_KEY.LEARNERS, {
        ...queryState,
        page: 1,
      });
    }
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      updateQueryStateTable(TABLE_KEY.LEARNERS, {
        ...queryState,
        page: 1,
        search: trimValue(newSearch),
      });
    }, 500),
    [],
  );

  return {
    learners,
    total,
    queryState,
    onClickRow,
    onClickAction,
    onSubmitDeleteLearner,
    searchValue,
    onChangeSearchValue,
  };
};
